import TB from '@tiger/bridge';
import { useMount } from '@tiger/hooks';
import { Button } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CardButtonWrap } from 'components/Card';
import PlaidLink from 'components/vendor/plaid/PlaidLink';
import { TaxLimitInfo } from 'modules/Deposit/type';
import successIcon from 'static/images/icons/complete.png';
import failIcon from 'static/images/icons/fail.png';
import APP from 'stores/APP';
import classList from 'utils/classlist';
import { printf } from 'utils/i18n';
import { lessVersion } from 'utils/util';
import Contribution from './Contribution';
import Native from './Native';

import './style.scss';

interface CompleteProps {
    isIRA?: boolean;
}

const Complete: FC<CompleteProps> = ({ isIRA }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const locationState = location.state as {
        amount: string;
        type: 'error' | 'success';
        error_code?: number;
        tax?: TaxLimitInfo;
        isManualBank?: boolean;
    } | null;
    const { error_code, type, amount, isManualBank = false } = locationState || {};
    const status = useMemo(() => {
        // 13037 持卡人信息不匹配
        let currStatus = error_code === 13037 ? 'errorName' : type;

        if (currStatus === 'success' && APP.isTiger) {
            currStatus = 'depositSuccessInTiger';
        }

        return currStatus;
    }, [error_code, type]);

    const statusMaps: Record<
        string,
        {
            msg: string;
            desc: string[];
            link: string;
            btn?: string;
        }
    > = useMemo(() => {
        return {
            error: {
                msg: __('We are unable to verify your bank account'),
                desc: [__('Please try using another bank account')],
                link: '../'
            },
            errorName: {
                msg: __('The name on your account does not match the name on the selected bank account'),
                desc: [__('Please select another bank account or contact your bank to update your bank information')],
                link: '../'
            },
            success: {
                msg: __('Congratulations'),
                desc: isManualBank
                    ? [printf(__('We have received your %s deposit request!'), amount || 0)]
                    : [
                          printf(
                              __(
                                  'We have received your %s deposit request! While your deposit is on its way, you can begin investing with the instant funding we have granted in your account.'
                              ),
                              amount || 0
                          ),
                          __(
                              'Once your deposit settles, those instant funds will no longer be available in your account.'
                          )
                      ],
                link: '/profile',
                btn: __('Done')
            }
        };
    }, [amount, isManualBank]);

    const onConfirm = useCallback(() => {
        if (APP.isTiger) {
            if (type === 'success' && !lessVersion('2.6.0')) {
                TB.goNativePage('/home/trade');

                return;
            }

            if (type === 'success' && window.TigerBridge.quit) {
                TB.quit();
            } else {
                // 需要跳转到对应的出入金页面
                navigate(statusMaps[status!].link);
            }
        } else {
            navigate(statusMaps[status!].link);
        }
    }, [navigate, status, type, statusMaps]);

    useMount(() => {
        window.addEventListener('popstate', onConfirm);

        return () => {
            window.removeEventListener('popstate', onConfirm);
        };
    });

    const depositSuccessInTiger = status === 'depositSuccessInTiger';

    if (!locationState) {
        return <Navigate to="../" replace />;
    }

    if (depositSuccessInTiger) {
        return <Native amount={amount!} onConfirm={onConfirm} />;
    }

    if (isIRA && locationState.tax) {
        return <Contribution year={locationState.tax.year} cost={locationState.tax.cost} onConfirm={onConfirm} />;
    }

    return (
        <div className="deposit-ach-complete-root">
            <div
                className={classList('ach-text-body', {
                    'ach-text-normal': !APP.isTiger
                })}>
                <p className="ach-status">
                    <img className="status-img" src={type === 'error' ? failIcon : successIcon} alt="" />
                    {statusMaps[status!].msg}
                </p>
                {statusMaps[status!].desc.map(item => (
                    <p className="desc" key={item}>
                        {item}
                    </p>
                ))}
                <CardButtonWrap className="btn-box">
                    {type !== 'error' && (
                        <Button type="primary" size="large" block onClick={onConfirm}>
                            {statusMaps[status!].btn}
                        </Button>
                    )}
                    {type === 'error' && (
                        <Link className="back" to={statusMaps.error.link}>
                            <Button type="primary" size="large">
                                {__('Back')}
                            </Button>
                        </Link>
                    )}
                    {type !== 'success' && (
                        <PlaidLink
                            btnProps={{
                                type: 'primary',
                                size: 'large'
                            }}
                            btnText={__('Add Another Bank Account')}
                            source="deposit"
                            errorInBtn
                        />
                    )}
                </CardButtonWrap>
            </div>
        </div>
    );
};

export default Complete;
